// =====================================================================
// WeddingOS – App.jsx  (UPDATED – alle fehlenden Routes ergänzt)
// =====================================================================

import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Suspense, lazy, useEffect } from "react";
import ErrorBoundary from "./components/ErrorBoundary";

// Bei jedem Routenwechsel an den Seitenanfang scrollen (SPA hält sonst die alte Position).
function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
}

// ── Gast (eager – kein Login nötig) ───────────────────────────────────
import Join           from "./pages/guest/Join";
import JoinLookup     from "./pages/guest/JoinLookup";      // NEU – /join ohne Token
import GuestHome      from "./pages/guest/Home";
import GuestMusic     from "./pages/guest/Music";
import GuestDrinks    from "./pages/guest/Drinks";
import GuestPhotos    from "./pages/guest/Photos";
import GuestGuestbook from "./pages/guest/Guestbook";
import GuestAnnounce  from "./pages/guest/Announce";

// ── Staff ──────────────────────────────────────────────────────────────
import DJView    from "./pages/dj/DJView";
import BarOrders from "./pages/bar/Orders";

// ── Venue (kein Login) ─────────────────────────────────────────────────
import VenueView from "./pages/venue/VenueView";            // NEU

// ── Lazy: Landing + Demo ───────────────────────────────────────────────
const LandingPage   = lazy(() => import("./pages/LandingPageV3"));
const LandingPageV4 = lazy(() => import("./pages/LandingPageV4"));
const HeroPreview   = lazy(() => import("./pages/HeroPreview"));
const DemoDashboard = lazy(() => import("./pages/demo/DemoDashboard"));

// ── Lazy: Public (kein Login) ─────────────────────────────────────────
const PublicMusicRequest = lazy(() => import("./pages/public/MusicRequest"));
const PublicCheckin      = lazy(() => import("./pages/public/Checkin"));

// ── Lazy: Auth ────────────────────────────────────────────────────────
const Login         = lazy(() => import("./pages/auth/Login"));
const Register      = lazy(() => import("./pages/auth/Register"));
const ResetPassword = lazy(() => import("./pages/auth/ResetPassword")); // NEU

// ── Lazy: Partner / Affiliate ─────────────────────────────────────────
const Partner        = lazy(() => import("./pages/Partner"));
const AffiliateStats = lazy(() => import("./pages/AffiliateStats"));
const AdminAffiliates = lazy(() => import("./pages/admin/Affiliates"));
const AdminContent     = lazy(() => import("./pages/admin/Content"));

// ── Lazy: Location-Produkt ────────────────────────────────────────────
const ForLocations      = lazy(() => import("./pages/ForLocations"));
const LocationsDirectory = lazy(() => import("./pages/LocationsDirectory"));
const LocationLogin     = lazy(() => import("./pages/location/Login"));
const LocationSignup    = lazy(() => import("./pages/location/Signup"));
const LocationDashboard = lazy(() => import("./pages/location/Dashboard"));

// ── Lazy: Rechtliches ─────────────────────────────────────────────────
const Impressum   = lazy(() => import("./pages/legal/Impressum"));
const Datenschutz = lazy(() => import("./pages/legal/Datenschutz"));
const AGB         = lazy(() => import("./pages/legal/AGB"));
const Widerruf    = lazy(() => import("./pages/legal/Widerruf"));

// ── Lazy: Dashboard ───────────────────────────────────────────────────
const DashboardLayout       = lazy(() => import("./pages/dashboard/Layout"));
const DashboardOverview     = lazy(() => import("./pages/dashboard/Overview"));
const DashboardMusic        = lazy(() => import("./pages/dashboard/Music"));
const DashboardDrinks       = lazy(() => import("./pages/dashboard/Drinks"));
const DashboardGuests       = lazy(() => import("./pages/dashboard/Guests"));
const DashboardSettings     = lazy(() => import("./pages/dashboard/Settings"));
const DashboardPhotos       = lazy(() => import("./pages/dashboard/Photos"));
const DashboardGuestbook    = lazy(() => import("./pages/dashboard/Guestbook"));
const DashboardAnnouncements= lazy(() => import("./pages/dashboard/Announcements")); // NEU
const DashboardPoster       = lazy(() => import("./pages/dashboard/Poster"));
const DashboardWiki         = lazy(() => import("./pages/dashboard/Wiki"));          // NEU
const DashboardFeedback     = lazy(() => import("./pages/dashboard/Feedback"));
const FeedbackPage          = lazy(() => import("./pages/FeedbackPage"));
const DashboardOnboarding   = lazy(() => import("./pages/dashboard/Onboarding"));   // NEU
const Admin                 = lazy(() => import("./pages/dashboard/Admin"));

// ── Suspense Fallback ─────────────────────────────────────────────────
function Loader() {
  return (
    <div style={{
      minHeight: "100dvh", display: "flex", alignItems: "center",
      justifyContent: "center", background: "var(--bg)",
    }}>
      <p style={{
        fontFamily: "var(--font-display)", fontStyle: "italic",
        fontSize: 13, letterSpacing: "0.3em", color: "var(--accent)",
      }}>unsereHochzeits.app</p>
    </div>
  );
}

function NotFound() {
  return (
    <div style={{ minHeight: "100dvh", display: "flex", alignItems: "center", justifyContent: "center", background: "var(--bg)", flexDirection: "column", gap: 16, fontFamily: "var(--font-body)" }}>
      <p style={{ fontFamily: "var(--font-display)", fontSize: "3rem", color: "var(--accent)" }}>✦</p>
      <h1 style={{ fontFamily: "var(--font-display)", fontWeight: 400, color: "var(--ink)" }}>Seite nicht gefunden</h1>
      <a href="/" style={{ color: "var(--accent)", textDecoration: "none", fontSize: 14 }}>← Zur Startseite</a>
    </div>
  );
}

// =====================================================================
export default function App() {
  return (
    <BrowserRouter>
      <ScrollToTop />
      <ErrorBoundary>
      <Suspense fallback={<Loader />}>
        <Routes>

          {/* ── Public ── */}
          <Route path="/"     element={<LandingPageV4 />} />
          <Route path="/story" element={<Navigate to="/" replace />} />
          <Route path="/v3"    element={<LandingPage />} />
          <Route path="/hero" element={<HeroPreview />} />
          <Route path="/demo" element={<DemoDashboard />} />

          {/* ── Public Quick-Actions (kein Login, WhatsApp/QR) ── */}
          <Route path="/music/:weddingId"   element={<PublicMusicRequest />} />
          <Route path="/checkin/:weddingId" element={<PublicCheckin />} />

          {/* ── Partner / Affiliate ── */}
          <Route path="/partner"        element={<Partner />} />
          <Route path="/partner/:code" element={<AffiliateStats />} />
          <Route path="/admin" element={<Navigate to="/dashboard/admin" replace />} />
          <Route path="/admin/affiliates" element={<AdminAffiliates />} />
          <Route path="/admin/content"    element={<AdminContent />} />

          {/* ── Location-Produkt ── */}
          <Route path="/locations"       element={<ForLocations />} />
          <Route path="/fuer-locations"  element={<ForLocations />} />{/* alt-URL bleibt (SEO/alte Links) */}
          <Route path="/hochzeitslocations" element={<LocationsDirectory />} />{/* öffentliches Partner-Verzeichnis */}
          <Route path="/location/login"  element={<LocationLogin />} />
          <Route path="/location/signup" element={<LocationSignup />} />
          <Route path="/location"        element={<LocationDashboard />} />

          {/* ── Auth ── */}
          <Route path="/impressum"      element={<Impressum />} />
          <Route path="/datenschutz"    element={<Datenschutz />} />
          <Route path="/agb"            element={<AGB />} />
          <Route path="/widerruf"       element={<Widerruf />} />

          <Route path="/login"          element={<Login />} />
          <Route path="/register"       element={<Register />} />
          <Route path="/reset-password" element={<ResetPassword />} />  {/* NEU */}

          {/* ── Gast ── */}
          <Route path="/join"            element={<JoinLookup />} />      {/* NEU */}
          <Route path="/join/:tableToken" element={<Join />} />
          <Route path="/guest"           element={<GuestHome />} />
          <Route path="/guest/music"     element={<GuestMusic />} />
          <Route path="/guest/drinks"    element={<GuestDrinks />} />
          <Route path="/guest/photos"    element={<GuestPhotos />} />
          <Route path="/guest/guestbook" element={<GuestGuestbook />} />
          <Route path="/guest/announce"  element={<GuestAnnounce />} />

          {/* ── Staff ── */}
          <Route path="/dj"    element={<DJView />} />
          <Route path="/bar"   element={<BarOrders />} />
          <Route path="/venue" element={<VenueView />} />                 {/* NEU */}

          {/* ── Dashboard (Auth-Guard via Layout) ── */}
          <Route element={<DashboardLayout />}>
            <Route path="/dashboard"                  element={<DashboardOverview />} />
            <Route path="/dashboard/onboarding"       element={<DashboardOnboarding />} />    {/* NEU */}
            <Route path="/dashboard/music"            element={<DashboardMusic />} />
            <Route path="/dashboard/drinks"           element={<DashboardDrinks />} />
            <Route path="/dashboard/guests"           element={<DashboardGuests />} />
            <Route path="/dashboard/settings"         element={<DashboardSettings />} />
            <Route path="/dashboard/photos"           element={<DashboardPhotos />} />
            <Route path="/dashboard/guestbook"        element={<DashboardGuestbook />} />
            <Route path="/dashboard/announcements"    element={<DashboardAnnouncements />} />  {/* NEU */}
            <Route path="/dashboard/poster"           element={<DashboardPoster />} />
            <Route path="/dashboard/feedback"         element={<DashboardFeedback />} />
            <Route path="/dashboard/wiki"             element={<DashboardWiki />} />           {/* NEU */}
            <Route path="/dashboard/admin"            element={<Admin />} />
          </Route>

          <Route path="/feedback/:weddingId" element={<FeedbackPage />} />

          {/* ── 404 ── */}
          <Route path="*" element={<NotFound />} />

        </Routes>
      </Suspense>
      </ErrorBoundary>
    </BrowserRouter>
  );
}
